import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import { IntlFormatters, useIntl, FormattedMessage } from 'react-intl'
import { Box, Row } from '../../legacy-design-system/form'
import { ExclusionFormData } from './shared'
import {
  FormTextContainer,
  Textarea,
  TextInput
} from '../../legacy-design-system/form-text'
import {
  ExclusionType,
  exclusionTypeI18nConfig,
  getExclusionTypes
} from './exclusion-type'
import { Dropdown } from '../../legacy-design-system/dropdown'
import { css } from '@emotion/css'
import {
  ExclusionInputType,
  exclusionInputTypeI18nConfig
} from './exclusion-input-type'
import { Label } from '../../legacy-design-system/form-label'
import { BasicTooltipContent } from '../../legacy-design-system/basic-tooltip-content'
import {
  allQueuePatternTypes,
  QueuePatternType,
  queuePatternTypeConfig
} from './queue-pattern-type'
import {
  allUrlPatternTypes,
  UrlPatternType,
  urlPatternTypeConfig
} from './url-pattern-type'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { MultiSelectCombobox } from '../../legacy-design-system/combobox'
import { GroupedRule } from './rule-group'
import {
  handleNewRule,
  handleNewAllRuleOption,
  getGroupedRuleItems,
  isAllRulesInferred,
  isAllProtectRulesFound,
  isAllAssessRulesFound
} from './rules-helper'
import { allRulesTypeI18n } from './all-rules'
import { AgentLanguageValue } from '../agent-language'
import { theme as deprecatedTheme } from '../../styled'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { ExclusionErrorForm } from './exclusion-form-manager'
import { BaseProtectRule } from '../protect-rules/shared'
import { BaseAssessRule } from '../assess-rules'
import { Checkbox } from '../../legacy-design-system/checkbox'
import {
  Text,
  TextProps
} from '@contrast-security-inc/design-system-typography'
import { Radio } from '@contrast-security-inc/design-system-inputs'

const styleOverridesCss = {
  outerContainerCss: css`
    width: unset;
  `,
  selectionContainerCss: css`
    border-radius: 3px;
    background-color: ${theme.color.white};
    background-image: unset;
    border-color: ${theme.color.neutral50};
  `
}

export const testIds = {
  exclusionName: 'exclusion-name-input',
  exclusionType: 'exclusion-type-dropdown',
  exclusionTypeMenu: 'exclusion-type-dropdown-menu',
  inputType: 'input-type-dropdown',
  inputName: 'input-name-input',
  urlPatternType: (value: UrlPatternType) => `url-pattern-type-${value}`,
  queuePatternType: (value: QueuePatternType) =>
    `queue-pattern-type-${value}-radio`,
  codes: 'codes-textarea',
  urls: 'urls-textarea',
  queues: 'queues-textarea',
  rules: 'rules-multiselect',
  footerSummary: 'form-footer-summary-msg',
  footerLanguages: 'form-footer-languages-msg',
  codesTooltipContent: 'codes-tooltip-content',
  suppressEventsCheckbox: 'suppress-events-checkbox'
}

export interface FormattedMessageProps extends TextProps {
  id: string
  values?: Record<string, ReactNode | (() => ReactNode)>
}

interface ExclusionFormProps {
  applicationLanguage: AgentLanguageValue
  exclusion: ExclusionFormData
  exclusionError: ExclusionErrorForm
  shouldSuppressEvents: boolean
  hasAttackEvent: boolean
  shouldAllowSuppressEvents?: boolean
  selectedRules: GroupedRule[]
  commonRules: BaseProtectRule[]
  assessOnlyRules: BaseAssessRule[]
  protectOnlyRules: BaseProtectRule[]
  setExclusion: Dispatch<SetStateAction<ExclusionFormData>>
  setSelectedRules: Dispatch<SetStateAction<GroupedRule[]>>
  toggleShouldSuppressEvents?: () => void
  getFormFieldProps: GetFormFieldPropsFn
}

export function ExclusionForm({
  applicationLanguage,
  exclusion: {
    exclusionName,
    exclusionType,
    inputType,
    inputName,
    urlPatternType,
    queuePatternType,
    codes,
    urls,
    queues
  },
  exclusionError,
  shouldSuppressEvents,
  hasAttackEvent,
  shouldAllowSuppressEvents = true,
  selectedRules,
  commonRules,
  assessOnlyRules,
  protectOnlyRules,
  setExclusion,
  setSelectedRules,
  toggleShouldSuppressEvents,
  getFormFieldProps
}: ExclusionFormProps) {
  const { formatMessage } = useIntl()

  const invalidRegexErrorMessage = formatMessage(
    { id: 'validation_invalidRegexPattern' },
    {
      a: (msg) => {
        return (
          <a
            href={formatMessage({
              id: 'link_regularExpression'
            })}
            target="_blank"
          >
            {msg}
          </a>
        )
      }
    }
  )

  const getRegexErrorMessage = (exclusion: string | undefined) => {
    return exclusion ? (invalidRegexErrorMessage as string) : undefined
  }

  const selectableExclusionTypes = hasAttackEvent
    ? []
    : getExclusionTypes(applicationLanguage)

  const allowedUrlPatternTypes: UrlPatternType[] =
    exclusionType === 'URL' ? ['ONLY'] : allUrlPatternTypes

  return (
    <>
      <FormTextContainer
        errorMessage={
          exclusionError.exclusionName
            ? formatMessage({ id: 'validation_invalidExclusionName' })
            : undefined
        }
      >
        {() => (
          <Row>
            <Box basis={12}>
              <TextInput
                data-testid={testIds.exclusionName}
                label={formatMessage({ id: 'title_exclusionName' })}
                value={exclusionName}
                onChange={(value) => {
                  setExclusion((prevState) => ({
                    ...prevState,
                    exclusionName: value
                  }))
                }}
                type="text"
                maxLength={255}
                required={true}
                showRequiredIndicator={false}
                {...getFormFieldProps({
                  id: 'exclusion_name',
                  getErrorMessage: () =>
                    formatMessage({
                      id: 'validation_requiredName'
                    })
                })}
              />
            </Box>
          </Row>
        )}
      </FormTextContainer>
      <Row>
        <Box basis={3}>
          <Label>{formatMessage({ id: 'title_exclusionType' })}</Label>
          {hasAttackEvent ? (
            <TextInput
              disabled={true}
              data-testid={testIds.exclusionType}
              label={undefined}
              value={formatMessage({
                id: exclusionTypeI18nConfig[exclusionType]
              })}
            />
          ) : (
            <Dropdown<ExclusionType>
              items={selectableExclusionTypes}
              selectedItem={exclusionType}
              onChange={(value) => {
                setExclusion((prevState) => ({
                  ...prevState,
                  exclusionType: value,
                  urlPatternType: getUrlPatternType(value),
                  queuePatternType: getQueuePatternType(value),
                  inputName: getInputName(value),
                  inputType: getInputType(value),
                  codes: getCodes(value),
                  urls: getUrls(
                    value,
                    prevState.urlPatternType,
                    prevState.urls
                  ),
                  queues: getQueues(
                    value,
                    prevState.queuePatternType,
                    prevState.queues
                  )
                }))
                setSelectedRules([])
              }}
              itemToString={(item) =>
                formatMessage({ id: exclusionTypeI18nConfig[item] })
              }
              styleOverrides={styleOverridesCss}
              unitTestId={testIds.exclusionType}
              menuE2eTestId={testIds.exclusionTypeMenu}
            />
          )}
        </Box>
        {exclusionType === 'INPUT' && (
          <Box basis={3}>
            <Label>{formatMessage({ id: 'title_inputType' })}</Label>
            {hasAttackEvent ? (
              inputType && (
                <TextInput
                  disabled={true}
                  data-testid={testIds.inputType}
                  label={undefined}
                  value={formatMessage({
                    id: exclusionInputTypeI18nConfig[inputType]
                  })}
                />
              )
            ) : (
              <Dropdown<ExclusionInputType>
                items={['PARAMETER', 'HEADER', 'QUERYSTRING', 'BODY', 'COOKIE']}
                selectedItem={inputType}
                onChange={(value) => {
                  setExclusion((prevState) => ({
                    ...prevState,
                    inputType: value,
                    inputName: shouldHideInputName(value)
                      ? undefined
                      : prevState.inputName
                  }))
                }}
                itemToString={(item) =>
                  formatMessage({ id: exclusionInputTypeI18nConfig[item] })
                }
                styleOverrides={styleOverridesCss}
                unitTestId={testIds.inputType}
              />
            )}
          </Box>
        )}
        {exclusionType === 'INPUT' &&
          inputType &&
          !shouldHideInputName(inputType) && (
            <Box basis={6}>
              <FormTextContainer
                errorMessage={getRegexErrorMessage(exclusionError.inputName)}
              >
                {() => (
                  <TextInput
                    disabled={hasAttackEvent}
                    renderLabel={() =>
                      exclusionType === 'INPUT' && (
                        <Label
                          children={formatMessage({
                            id: 'title_inputName'
                          })}
                          renderHelpContent={() => (
                            <BasicTooltipContent
                              i18nTitle="title_inputName"
                              i18nDescription="detail_inputNameExclusionDescription"
                            />
                          )}
                        />
                      )
                    }
                    data-testid={testIds.inputName}
                    value={inputName}
                    onChange={(value) => {
                      setExclusion((prevState) => ({
                        ...prevState,
                        inputName: value
                      }))
                    }}
                    type="text"
                    maxLength={255}
                    required={true}
                    {...getFormFieldProps({
                      id: 'exclusion_input_name',
                      getErrorMessage: () =>
                        formatMessage({
                          id: 'validation_requiredInputName'
                        })
                    })}
                  />
                )}
              </FormTextContainer>
            </Box>
          )}
      </Row>

      {exclusionType === 'CODE' && (
        <>
          <Row>
            <Box basis={12}>
              <div
                className={css`
                  display: flex;
                  flex-direction: row;
                `}
              >
                <Label
                  children={formatMessage({
                    id: 'title_codes'
                  })}
                  renderHelpContent={() => (
                    <div
                      data-testid={testIds.codesTooltipContent}
                      className={css`
                        margin: 0;
                        max-width: 330px;
                        overflow-wrap: normal;
                        white-space: normal;
                        font-weight: ${deprecatedTheme.fontWeights.primary
                          .regular};
                      `}
                    >
                      {getCodesTooltip(applicationLanguage, formatMessage)}
                    </div>
                  )}
                />
                <Text
                  font="metadata"
                  color="neutral70"
                  className={css`
                    display: flex;
                    flex: 1;
                    justify-content: flex-end;
                    padding-right: 4px;
                  `}
                >
                  <FormattedMessage id="detail_oneCodeReferencePerLine" />
                </Text>
              </div>
            </Box>
          </Row>
          <FormTextContainer errorMessage={exclusionError.codes}>
            {() => (
              <Row>
                <Box basis={12}>
                  <Textarea
                    data-testid={testIds.codes}
                    expand
                    rows={5}
                    required
                    placeholder={formatMessage({
                      id: 'placeholder_codeToSuppressFindings'
                    })}
                    value={codes}
                    onChange={(value) => {
                      setExclusion((prevState) => ({
                        ...prevState,
                        codes: value
                      }))
                    }}
                    {...getFormFieldProps({
                      id: 'exclusion_codes',
                      getErrorMessage: () =>
                        formatMessage({
                          id: 'validation_requiredCodes'
                        })
                    })}
                  />
                </Box>
              </Row>
            )}
          </FormTextContainer>
        </>
      )}

      {(exclusionType === 'INPUT' || exclusionType === 'URL') && (
        <>
          <Row>
            <Box basis={12}>
              <Label
                children={formatMessage({
                  id: 'title_appliedURLs'
                })}
                renderHelpContent={() => (
                  <p
                    className={css`
                      max-width: 270px;
                      white-space: normal;
                    `}
                  >
                    <FormattedMessage
                      id="detail_appliedURLsExclusionDescription"
                      values={{ br: <br /> }}
                    />
                  </p>
                )}
              />
              {allowedUrlPatternTypes.map((type, index) => (
                <li
                  className={css`
                    margin-top: 4px;
                    display: flex;
                  `}
                  key={index}
                >
                  <Radio
                    onChange={() =>
                      setExclusion((prevState) => ({
                        ...prevState,
                        urlPatternType: type,
                        urls:
                          urlPatternType === 'ONLY' && type === 'ONLY'
                            ? prevState.urls
                            : undefined
                      }))
                    }
                    isChecked={urlPatternType === type}
                    label={formatMessage({
                      id: urlPatternTypeConfig[type].i18n
                    })}
                    testId={testIds.urlPatternType(type)}
                  />

                  {urlPatternType === 'ONLY' && type === 'ONLY' && (
                    <Text
                      font="metadata"
                      color="neutral70"
                      className={css`
                        display: flex;
                        flex: 1;
                        justify-content: flex-end;
                        padding-right: 4px;
                      `}
                    >
                      <FormattedMessage id="detail_oneURLPerLine" />
                    </Text>
                  )}
                </li>
              ))}
            </Box>
          </Row>

          {urlPatternType === 'ONLY' && (
            <FormTextContainer
              errorMessage={getRegexErrorMessage(exclusionError.urls)}
            >
              {() => (
                <Row>
                  <Box basis={12}>
                    <Textarea
                      data-testid={testIds.urls}
                      expand
                      rows={5}
                      required
                      value={urls}
                      onChange={(value) => {
                        setExclusion((prevState) => ({
                          ...prevState,
                          urls: value
                        }))
                      }}
                      {...getFormFieldProps({
                        id: 'exclusion_urls',
                        getErrorMessage: () =>
                          formatMessage({
                            id: 'validation_requiredURLs'
                          })
                      })}
                    />
                  </Box>
                </Row>
              )}
            </FormTextContainer>
          )}
        </>
      )}

      {exclusionType === 'QUEUE' && (
        <>
          <Row>
            <Box basis={12}>
              <Label
                children={formatMessage({
                  id: 'title_appliedQueues'
                })}
                renderHelpContent={() => (
                  <p
                    className={css`
                      max-width: 270px;
                      white-space: normal;
                    `}
                  >
                    <FormattedMessage
                      id="detail_appliedQueuesExclusionDescription"
                      values={{ br: <br /> }}
                    />
                  </p>
                )}
              />
              {allQueuePatternTypes.map((type, index) => (
                <li
                  className={css`
                    margin-top: 4px;
                    display: flex;
                  `}
                  key={index}
                >
                  <Radio
                    label={formatMessage({
                      id: queuePatternTypeConfig[type].i18n
                    })}
                    isChecked={queuePatternType === type}
                    onChange={() =>
                      setExclusion((prevState) => ({
                        ...prevState,
                        queuePatternType: type,
                        queues:
                          queuePatternType === 'ONLY' && type === 'ONLY'
                            ? prevState.queues
                            : undefined
                      }))
                    }
                    testId={testIds.queuePatternType(type)}
                  />

                  {queuePatternType === 'ONLY' && type === 'ONLY' && (
                    <Text
                      font="metadata"
                      color="neutral70"
                      className={css`
                        display: flex;
                        flex: 1;
                        justify-content: flex-end;
                        padding-right: 4px;
                      `}
                    >
                      <FormattedMessage id="detail_oneQueueNamePerLine" />
                    </Text>
                  )}
                </li>
              ))}
            </Box>
          </Row>

          {queuePatternType === 'ONLY' && (
            <FormTextContainer errorMessage={exclusionError.queues}>
              {() => (
                <Row>
                  <Box basis={12}>
                    <Textarea
                      data-testid={testIds.queues}
                      expand
                      rows={5}
                      required
                      value={queues}
                      onChange={(value) => {
                        setExclusion((prevState) => ({
                          ...prevState,
                          queues: value
                        }))
                      }}
                      {...getFormFieldProps({
                        id: 'exclusion_queues',
                        getErrorMessage: () =>
                          formatMessage({
                            id: 'validation_requiredQueues'
                          })
                      })}
                    />
                  </Box>
                </Row>
              )}
            </FormTextContainer>
          )}
        </>
      )}

      <Row>
        <Box basis={12}>
          <MultiSelectCombobox<GroupedRule>
            customComboboxInputTestID={testIds.rules}
            label={formatMessage({
              id: 'title_applicableRules'
            })}
            noResultsText={formatMessage({
              id: 'detail_noResultsFound'
            })}
            items={getGroupedRuleItems(
              commonRules,
              assessOnlyRules,
              protectOnlyRules,
              selectedRules,
              formatMessage
            )}
            itemToString={({ name }) => name}
            placeHolderTag={formatMessage({ id: allRulesTypeI18n.ALL })}
            onChange={(groupedRules) => {
              let newSelectedRules = [...groupedRules]

              if (newSelectedRules.length > 1) {
                const newRule = newSelectedRules[newSelectedRules.length - 1]
                newSelectedRules = newRule.isAllOption
                  ? handleNewAllRuleOption(newSelectedRules)
                  : handleNewRule(newSelectedRules)
              }

              setSelectedRules(newSelectedRules)
            }}
            values={selectedRules}
            itemToID={(rule) => `rule-${rule.value}`}
            itemsEqual={(first, second) => first.value === second.value}
          />
        </Box>
      </Row>

      <Row>
        <Box basis={12}>
          <Text data-testid={testIds.footerSummary} font="metadata">
            <FormattedMessage
              {...getFooterMsgProps({
                exclusionType,
                selectedRules,
                urlPatternType,
                inputName,
                inputType,
                queuePatternType
              })}
            />
          </Text>
          <Text
            data-testid={testIds.footerLanguages}
            font="metadata"
            as="div"
            className={css`
              margin-top: 4px;
            `}
          >
            <FormattedMessage id={getFooterSupportedLangMsg(exclusionType)} />
          </Text>
        </Box>
      </Row>

      {shouldAllowSuppressEvents &&
        toggleShouldSuppressEvents &&
        exclusionType !== 'QUEUE' && (
          <Row>
            <Box basis={12}>
              <Checkbox
                data-testid={testIds.suppressEventsCheckbox}
                value={shouldSuppressEvents}
                label={formatMessage({
                  id: 'action_attackEventExclusion_suppressMatchingEvents'
                })}
                onChange={toggleShouldSuppressEvents}
                renderHelpContent={() => (
                  <FormattedMessage id="detail_appExclusion_suppressEventsForAssess" />
                )}
              />
            </Box>
          </Row>
        )}
    </>
  )
}

export const getFooterMsgProps = ({
  exclusionType,
  selectedRules,
  urlPatternType,
  inputName,
  inputType,
  queuePatternType
}: Pick<
  ExclusionFormData,
  | 'exclusionType'
  | 'urlPatternType'
  | 'inputName'
  | 'inputType'
  | 'queuePatternType'
> & {
  selectedRules: GroupedRule[]
}): Pick<FormattedMessageProps, 'id' | 'values'> => {
  switch (exclusionType) {
    case 'INPUT':
      return {
        id: 'detail_appExclusion_summaryForInputExclusion',
        values: {
          allOrTheSpecifiedUrls: (
            <FormattedMessage
              id={getUrlsFooterMsg(urlPatternType as UrlPatternType)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          allOrTheSpecifiedRules: (
            <FormattedMessage
              id={getRulesFooterMsg(selectedRules)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          inputType: (
            <FormattedMessage
              id={exclusionInputTypeI18nConfig[inputType as ExclusionInputType]}
            />
          ),
          inputName: inputName ? ` "${inputName}"` : null,
          b: (...msg) => <strong>{msg}</strong>
        }
      }

    case 'URL':
      return {
        id: 'detail_appExclusion_summaryForUrlExclusion',
        values: {
          allOrTheSpecifiedRules: (
            <FormattedMessage
              id={getRulesFooterMsg(selectedRules)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          b: (...msg) => <strong>{msg}</strong>
        }
      }
    case 'CODE':
      return {
        id: 'detail_appExclusion_summaryForCodeExclusion',
        values: {
          allOrTheSpecifiedRules: (
            <FormattedMessage
              id={getRulesFooterMsg(selectedRules)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          b: (...msg) => <strong>{msg}</strong>
        }
      }
    case 'QUEUE':
      return {
        id: 'detail_appExclusion_summaryForQueueExclusion',
        values: {
          allOrTheSpecifiedQueues: (
            <FormattedMessage
              id={getQueueFooterMsg(queuePatternType as QueuePatternType)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          allOrTheSpecifiedRules: (
            <FormattedMessage
              id={getRulesFooterMsg(selectedRules)}
              values={{
                b: (...msg) => <strong>{msg}</strong>
              }}
            />
          ),
          b: (...msg) => <strong>{msg}</strong>
        }
      }
  }
}

export const getRulesFooterMsg = (selectedRules: GroupedRule[]) => {
  if (
    isAllRulesInferred(selectedRules) ||
    (isAllAssessRulesFound(selectedRules) &&
      isAllProtectRulesFound(selectedRules))
  ) {
    return `detail_appExclusion_summary_allRules`
  } else if (
    isAllProtectRulesFound(selectedRules) &&
    selectedRules.length === 1
  ) {
    return `detail_appExclusion_summary_allProtectRules`
  } else if (
    isAllAssessRulesFound(selectedRules) &&
    selectedRules.length === 1
  ) {
    return `detail_appExclusion_summary_allAssessRules`
  } else {
    return `detail_appExclusion_summary_specifiedRules`
  }
}

export const getQueueFooterMsg = (queuePatternType: QueuePatternType) => {
  return queuePatternType === 'ALL'
    ? 'detail_appExclusion_summary_allQueues'
    : 'detail_appExclusion_summary_specifiedQueues'
}

export const getUrlsFooterMsg = (urlPatternType: UrlPatternType) => {
  // eslint-disable-next-line unicorn/prefer-ternary
  if (urlPatternType === 'ALL') {
    return `detail_appExclusion_summary_allUrls`
  } else {
    return `detail_appExclusion_summary_specifiedUrls`
  }
}

export const getCodesTooltip = (
  language: AgentLanguageValue,
  formatMessage: IntlFormatters['formatMessage']
) => {
  const tagValues = {
    p: (...msg) => <p>{msg}</p>,
    strong: (...msg) => <strong>{msg}</strong>
  }

  switch (language) {
    case '.NET':
    case '.NET Core':
      return formatMessage(
        {
          id: 'variable_codeAppExclusion_description'
        },
        {
          ...tagValues,
          methodName: 'DoLegacySecurity()',
          className: 'Com.Acme.OldSecurity',
          codeLine: 'Com.Acme.OldSecurity.DoLegacySecurity'
        }
      )
    case 'Ruby':
      return formatMessage(
        {
          id: 'variable_codeAppExclusion_description_ruby'
        },
        {
          ...tagValues,
          methodName: 'do_legacy_security',
          fileName: '/Ruby-Gem/com/acme/old_security.rb',
          codeBlock: '/Ruby-Gem/com/acme/old_security.rb:do_legacy_security'
        }
      )
    default:
      return formatMessage(
        {
          id: 'variable_codeAppExclusion_description'
        },
        {
          ...tagValues,
          methodName: 'doLegacySecurity()',
          className: 'com.acme.OldSecurity',
          codeLine: 'com.acme.OldSecurity.doLegacySecurity'
        }
      )
  }
}

export function shouldHideInputName(inputType: ExclusionInputType) {
  return inputType === 'QUERYSTRING' || inputType === 'BODY'
}

export function getUrlPatternType(
  exclusionType: ExclusionType
): UrlPatternType | undefined {
  switch (exclusionType) {
    case 'URL':
      return 'ONLY'
    case 'INPUT':
      return 'ALL'
    case 'CODE':
    case 'QUEUE':
    default:
      return undefined
  }
}

export function getQueuePatternType(
  exclusionType: ExclusionType
): QueuePatternType | undefined {
  return exclusionType === 'QUEUE' ? 'ALL' : undefined
}

export function getInputName(exclusionType: ExclusionType): string | undefined {
  return exclusionType !== 'INPUT' ? undefined : ''
}

export function getInputType(
  exclusionType: ExclusionType
): ExclusionInputType | undefined {
  return exclusionType !== 'INPUT' ? undefined : 'PARAMETER'
}

export function getCodes(exclusionType: ExclusionType): string | undefined {
  return exclusionType !== 'CODE' ? undefined : ''
}

export function getUrls(
  exclusionType: ExclusionType,
  urlPatternType: UrlPatternType | undefined,
  urls: string | undefined
): string | undefined {
  return !['CODE', 'QUEUE'].includes(exclusionType) && urlPatternType === 'ONLY'
    ? urls
    : undefined
}

export function getQueues(
  exclusionType: ExclusionType,
  queuePatternType: QueuePatternType | undefined,
  queues: string | undefined
): string | undefined {
  return exclusionType === 'QUEUE' && queuePatternType === 'ONLY'
    ? queues
    : undefined
}

export function getFooterSupportedLangMsg(
  exclusionType: ExclusionType
): string {
  switch (exclusionType) {
    case 'URL':
      return 'detail_appExclusion_urlExclusionSupportedLanguages'
    case 'INPUT':
      return 'detail_appExclusion_inputExclusionSupportedLanguages'
    case 'CODE':
      return 'detail_appExclusion_codeExclusionSupportedLanguages'
    case 'QUEUE':
      return 'detail_appExclusion_queueExclusionSupportedLanguages'
    default:
      return ''
  }
}
